'use client';
import React from 'react';
import { Cable } from 'lucide-react';
import type { ComponentType, WireColor } from './circuitTypes';
import { COMPONENT_DEFINITIONS, WIRE_COLOR_MAP } from './circuitTypes';

// Polished SVG icons for each component type
const ComponentIcon = ({ type }: { type: ComponentType }) => {
  const base = "w-7 h-7 flex-shrink-0";
  switch (type) {
    case 'supply':
      return (
        <svg className={base} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="8" width="22" height="12" rx="2" fill="#fef3c7" stroke="#f59e0b" strokeWidth="1.5"/>
          <line x1="14" y1="3" x2="14" y2="8" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="14" y1="20" x2="14" y2="25" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M11 12l2 4h2l2-4" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="14" cy="14" r="2" fill="#f59e0b"/>
        </svg>
      );
    case 'lamp':
      return (
        <svg className={base} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 17c0-2.2 1.8-4 4-4s4 1.8 4 4v1h-8v-1z" fill="#fde68a" stroke="#f59e0b" strokeWidth="1.3"/>
          <rect x="11" y="18" width="6" height="2" rx="0.5" fill="#d1d5db" stroke="#9ca3af" strokeWidth="1"/>
          <line x1="14" y1="20" x2="14" y2="23" stroke="#9ca3af" strokeWidth="1.3" strokeLinecap="round"/>
          <line x1="14" y1="7" x2="14" y2="13" stroke="#f59e0b" strokeWidth="1.3" strokeLinecap="round"/>
          <line x1="8" y1="10" x2="10.5" y2="12" stroke="#fbbf24" strokeWidth="1" strokeLinecap="round"/>
          <line x1="20" y1="10" x2="17.5" y2="12" stroke="#fbbf24" strokeWidth="1" strokeLinecap="round"/>
          <line x1="7" y1="14" x2="10" y2="14" stroke="#fbbf24" strokeWidth="1" strokeLinecap="round"/>
          <line x1="21" y1="14" x2="18" y2="14" stroke="#fbbf24" strokeWidth="1" strokeLinecap="round"/>
        </svg>
      );
    case 'switch-one-way':
      return (
        <svg className={base} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="7" cy="14" r="2.5" fill="#e0f2fe" stroke="#0284c7" strokeWidth="1.3"/>
          <circle cx="21" cy="14" r="2.5" fill="#e0f2fe" stroke="#0284c7" strokeWidth="1.3"/>
          <line x1="9.5" y1="14" x2="18.5" y2="9" stroke="#0284c7" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="3" y1="14" x2="4.5" y2="14" stroke="#0284c7" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="23.5" y1="14" x2="25" y2="14" stroke="#0284c7" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      );
    case 'switch-two-way':
      return (
        <svg className={base} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="7" cy="14" r="2.5" fill="#ede9fe" stroke="#7c3aed" strokeWidth="1.3"/>
          <circle cx="21" cy="10" r="2" fill="#ede9fe" stroke="#7c3aed" strokeWidth="1.3"/>
          <circle cx="21" cy="18" r="2" fill="#ede9fe" stroke="#7c3aed" strokeWidth="1.3"/>
          <line x1="9.5" y1="13" x2="19" y2="10.5" stroke="#7c3aed" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="9.5" y1="15" x2="19" y2="17.5" stroke="#7c3aed" strokeWidth="1" strokeLinecap="round" strokeDasharray="2 1.5"/>
          <line x1="3" y1="14" x2="4.5" y2="14" stroke="#7c3aed" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="23" y1="10" x2="25" y2="10" stroke="#7c3aed" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="23" y1="18" x2="25" y2="18" stroke="#7c3aed" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      );
    case 'socket':
      return (
        <svg className={base} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="4" y="5" width="20" height="18" rx="2.5" fill="#f0fdf4" stroke="#16a34a" strokeWidth="1.5"/>
          <circle cx="10" cy="12" r="2" fill="white" stroke="#16a34a" strokeWidth="1.2"/>
          <circle cx="18" cy="12" r="2" fill="white" stroke="#16a34a" strokeWidth="1.2"/>
          <rect x="11.5" y="16" width="5" height="3" rx="1" fill="white" stroke="#16a34a" strokeWidth="1.2"/>
          <line x1="10" y1="10" x2="10" y2="9" stroke="#16a34a" strokeWidth="1.2" strokeLinecap="round"/>
          <line x1="18" y1="10" x2="18" y2="9" stroke="#16a34a" strokeWidth="1.2" strokeLinecap="round"/>
        </svg>
      );
    case 'junction-box':
      return (
        <svg className={base} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="6" y="6" width="16" height="16" rx="2" fill="#f8fafc" stroke="#64748b" strokeWidth="1.5"/>
          <line x1="14" y1="2" x2="14" y2="6" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="14" y1="22" x2="14" y2="26" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="2" y1="14" x2="6" y2="14" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="22" y1="14" x2="26" y2="14" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round"/>
          <circle cx="14" cy="14" r="3" fill="#e2e8f0" stroke="#64748b" strokeWidth="1.2"/>
          <circle cx="14" cy="14" r="1" fill="#64748b"/>
        </svg>
      );
    case 'mcb':
      return (
        <svg className={base} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="7" y="3" width="14" height="22" rx="2" fill="#eff6ff" stroke="#2563eb" strokeWidth="1.5"/>
          <rect x="10" y="7" width="8" height="5" rx="1" fill="#bfdbfe" stroke="#2563eb" strokeWidth="1"/>
          <rect x="11.5" y="9" width="5" height="7" rx="1" fill="#2563eb"/>
          <line x1="14" y1="3" x2="14" y2="1" stroke="#2563eb" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="14" y1="25" x2="14" y2="27" stroke="#2563eb" strokeWidth="1.5" strokeLinecap="round"/>
          <text x="14" y="22" textAnchor="middle" fontSize="5" fill="#2563eb" fontWeight="bold">MCB</text>
        </svg>
      );
    case 'rcbo':
      return (
        <svg className={base} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="6" y="3" width="16" height="22" rx="2" fill="#fff7ed" stroke="#ea580c" strokeWidth="1.5"/>
          <rect x="9" y="7" width="10" height="5" rx="1" fill="#fed7aa" stroke="#ea580c" strokeWidth="1"/>
          <rect x="11" y="9" width="6" height="7" rx="1" fill="#ea580c"/>
          <path d="M10 18 Q14 16 18 18" stroke="#ea580c" strokeWidth="1.2" fill="none" strokeLinecap="round"/>
          <line x1="14" y1="3" x2="14" y2="1" stroke="#ea580c" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="14" y1="25" x2="14" y2="27" stroke="#ea580c" strokeWidth="1.5" strokeLinecap="round"/>
          <text x="14" y="23" textAnchor="middle" fontSize="4.5" fill="#ea580c" fontWeight="bold">RCBO</text>
        </svg>
      );
    case 'fuse':
      return (
        <svg className={base} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="8" y="10" width="12" height="8" rx="4" fill="#fef9c3" stroke="#ca8a04" strokeWidth="1.5"/>
          <line x1="3" y1="14" x2="8" y2="14" stroke="#ca8a04" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="20" y1="14" x2="25" y2="14" stroke="#ca8a04" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="11" y1="14" x2="17" y2="14" stroke="#ca8a04" strokeWidth="1.2" strokeLinecap="round" strokeDasharray="1.5 1"/>
          <circle cx="11" cy="14" r="1" fill="#ca8a04"/>
          <circle cx="17" cy="14" r="1" fill="#ca8a04"/>
        </svg>
      );
    case 'neutral-bar':
      return (
        <svg className={base} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="4" y="11" width="20" height="6" rx="1.5" fill="#dbeafe" stroke="#1d4ed8" strokeWidth="1.5"/>
          <line x1="8" y1="8" x2="8" y2="11" stroke="#1d4ed8" strokeWidth="1.3" strokeLinecap="round"/>
          <line x1="12" y1="8" x2="12" y2="11" stroke="#1d4ed8" strokeWidth="1.3" strokeLinecap="round"/>
          <line x1="16" y1="8" x2="16" y2="11" stroke="#1d4ed8" strokeWidth="1.3" strokeLinecap="round"/>
          <line x1="20" y1="8" x2="20" y2="11" stroke="#1d4ed8" strokeWidth="1.3" strokeLinecap="round"/>
          <line x1="8" y1="17" x2="8" y2="20" stroke="#1d4ed8" strokeWidth="1.3" strokeLinecap="round"/>
          <line x1="12" y1="17" x2="12" y2="20" stroke="#1d4ed8" strokeWidth="1.3" strokeLinecap="round"/>
          <line x1="16" y1="17" x2="16" y2="20" stroke="#1d4ed8" strokeWidth="1.3" strokeLinecap="round"/>
          <line x1="20" y1="17" x2="20" y2="20" stroke="#1d4ed8" strokeWidth="1.3" strokeLinecap="round"/>
          <text x="14" y="15.5" textAnchor="middle" fontSize="4.5" fill="#1d4ed8" fontWeight="bold">N</text>
        </svg>
      );
    case 'earth-bar':
      return (
        <svg className={base} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="4" y="11" width="20" height="6" rx="1.5" fill="#dcfce7" stroke="#15803d" strokeWidth="1.5"/>
          <line x1="8" y1="8" x2="8" y2="11" stroke="#15803d" strokeWidth="1.3" strokeLinecap="round"/>
          <line x1="12" y1="8" x2="12" y2="11" stroke="#15803d" strokeWidth="1.3" strokeLinecap="round"/>
          <line x1="16" y1="8" x2="16" y2="11" stroke="#15803d" strokeWidth="1.3" strokeLinecap="round"/>
          <line x1="20" y1="8" x2="20" y2="11" stroke="#15803d" strokeWidth="1.3" strokeLinecap="round"/>
          <line x1="8" y1="17" x2="8" y2="20" stroke="#15803d" strokeWidth="1.3" strokeLinecap="round"/>
          <line x1="12" y1="17" x2="12" y2="20" stroke="#15803d" strokeWidth="1.3" strokeLinecap="round"/>
          <line x1="16" y1="17" x2="16" y2="20" stroke="#15803d" strokeWidth="1.3" strokeLinecap="round"/>
          <line x1="20" y1="17" x2="20" y2="20" stroke="#15803d" strokeWidth="1.3" strokeLinecap="round"/>
          <text x="14" y="15.5" textAnchor="middle" fontSize="4.5" fill="#15803d" fontWeight="bold">E</text>
        </svg>
      );
    default:
      return <div className={`${base} bg-gray-100 rounded`} />;
  }
};

const COMPONENT_LIST: { type: ComponentType; description: string }[] = [
  { type: 'supply', description: 'Mains 230V supply' },
  { type: 'lamp', description: 'Incandescent / LED lamp' },
  { type: 'switch-one-way', description: 'Single-pole switch' },
  { type: 'switch-two-way', description: '2-way (staircase) switch' },
  { type: 'socket', description: '13A socket outlet' },
  { type: 'junction-box', description: '4-terminal junction box' },
  { type: 'mcb', description: 'Miniature circuit breaker' },
  { type: 'rcbo', description: 'RCD + overcurrent protection' },
  { type: 'fuse', description: 'Rewireable / cartridge fuse' },
  { type: 'neutral-bar', description: 'Neutral distribution bar' },
  { type: 'earth-bar', description: 'Earth / CPC bar' },
];

interface Props {
  selectedColor: WireColor;
  onColorChange: (c: WireColor) => void;
}

export default function ComponentLibrarySidebar({ selectedColor, onColorChange }: Props) {
  const handleDragStart = (e: React.DragEvent, type: ComponentType) => {
    e.dataTransfer.setData('componentType', type);
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <aside className="w-56 bg-white border-r border-border flex flex-col flex-shrink-0 overflow-hidden">
      {/* Header */}
      <div className="px-3 py-3 border-b border-border">
        <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-0.5">Components</h2>
        <p className="text-xs text-muted-foreground/70">Drag onto canvas</p>
      </div>

      {/* Component list */}
      <div className="flex-1 overflow-y-auto py-2">
        {COMPONENT_LIST.map((item) => {
          const def = COMPONENT_DEFINITIONS[item.type];
          return (
            <div
              key={`lib-${item.type}`}
              draggable
              onDragStart={(e) => handleDragStart(e, item.type)}
              className="component-card flex items-center gap-2.5 px-3 py-2.5 mx-2 rounded-lg hover:bg-primary/5 hover:border-primary/20 border border-transparent transition-all duration-150 mb-0.5 group cursor-grab active:cursor-grabbing"
              title={item.description}
            >
              <ComponentIcon type={item.type} />
              <div className="min-w-0">
                <div className="text-xs font-semibold text-foreground truncate">{def.label}</div>
                <div className="text-xs text-muted-foreground/70 truncate">{item.description}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Wire colour picker */}
      <div className="border-t border-border px-3 py-3">
        <div className="flex items-center gap-1.5 mb-2">
          <Cable size={13} className="text-muted-foreground" />
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Wire Colour</span>
        </div>
        <div className="flex flex-col gap-1.5">
          {(Object.keys(WIRE_COLOR_MAP) as WireColor[]).map((color) => {
            const cfg = WIRE_COLOR_MAP[color];
            const isSelected = selectedColor === color;
            return (
              <button
                key={`wire-${color}`}
                onClick={() => onColorChange(color)}
                className={`flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-medium border transition-all duration-150 w-full text-left
                  ${isSelected
                    ? 'border-current bg-slate-50 shadow-sm'
                    : 'border-border hover:bg-muted'
                  }`}
                style={isSelected ? { color: cfg.hex, borderColor: cfg.hex } : {}}
              >
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0 ring-1 ring-current/30"
                  style={{ backgroundColor: cfg.hex }}
                />
                {cfg.label}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground/60 mt-2 leading-relaxed">
          Select colour before drawing a wire
        </p>
      </div>
    </aside>
  );
}